import styled from "@emotion/styled";
import { deleteMeetingReservation, TReservation } from "apis/meetings/meeting-reservation";
import { useEffect, useRef, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";
import { useToast } from "hook/useToast";
import DeleteWarningModal from "pages/meeting/my-booking/delete-modal";
import { LoadingModal } from "uis/modal";
import { sleep } from "utils/validation";
import * as Modal from "uis/modal";
import { UtilsService } from "utils/utils.service";
import { RRuleService } from "utils/rrule.service";
import { getKorDate } from "utils/date";
import { RepeatReservationDeleteOption } from "uis/modal/repeat-reservation-delete-modal/types";

type Props = {
  meetingRoom?: TMeetingRoom;
  reservationInfo?: TReservation;
  onHandleEdit?: (reservation?: TReservation, meetingRoom?: TMeetingRoom, isMyReservation?: boolean) => void;
  setOpen: (open: boolean) => void;
  open: boolean;
  top?: number;
  left?: number;
  right?: number;
  color: string;
  canEdit: boolean;
  isMyReservation?: boolean;
  onUpdate?: () => void;
};

const ReservationInfoModal = ({
  isMyReservation,
  reservationInfo,
  setOpen,
  open,
  color,
  meetingRoom,
  onHandleEdit,
  top,
  left,
  right,
  canEdit,
  onUpdate,
}: Props) => {
  const { showToast } = useToast();
  const memberInfo = useRecoilValue(member);
  const ref = useRef<HTMLDivElement>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [endRrule, setEndRrule] = useState<Date | null>(null);
  const utilsService = new UtilsService();
  const rruleService = new RRuleService(utilsService);

  const removeHtmlTag = (input: string) => {
    //* INFO: 정규식: <[^>]*>는 모든 HTML 태그를 찾습니다.
    return input.replace(/<[^>]*>/g, "");
  };

  const onConfirmDelete = async (options?: RepeatReservationDeleteOption) => {
    setOpenDeleteModal(false);
    setLoading(true);

    const deleteItem = {
      googleEventId: reservationInfo?.googleEventId ?? "",
      meetingReservationId: reservationInfo?.id ?? "",
      meetingRoomId: reservationInfo?.meetingRoom?.id ?? "",
      editOption: options,
    };

    const res = await deleteMeetingReservation(deleteItem);
    if (res.success) {
      if (meetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE") {
        await sleep(4000);
      }
      showToast("삭제가 완료되었습니다.", "success");
      onUpdate?.();
    } else {
      showToast(res.message ?? "처리 중 오류가 발생했습니다. 다시 시도해주세요.", "error");
    }

    setOpen(false);
    setLoading(false);
  };

  const formatTimeRange = (start: string, end: string): string => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const pad = (num: number): string => num.toString().padStart(2, "0");
    const startTime = `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}`;
    const endTime = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

    return `${startTime} - ${endTime}`;
  };

  //* INFO: 오전 7시 - 오후 11시에 속한 예약인지 여부 확인
  const isInTimeRange = (start: string, end: string): boolean => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const startHour = startDate.getHours();
    const endHour = endDate.getHours();

    if (startHour >= 7 && endHour <= 23 && startDate.setHours(0, 0, 0, 0) === endDate.setHours(0, 0, 0, 0)) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (open && reservationInfo?.rrule) {
      const period = rruleService.getPeriodOfRecurrence(
        reservationInfo?.rrule,
        new Date(reservationInfo?.recurringStartDate ?? "")
      );
      setEndRrule(period.terminatedAt);
    }
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  return (
    <Container style={{ display: open ? "flex" : "none", top: top, left: left, right: right }} ref={ref}>
      <div
        style={{ position: "absolute", top: "20px", right: "20px", cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen?.(false);
        }}
      >
        <UI.IconButton.Normal type="primary" icon="x" size={20} color="var(--alias-color-label-normal)" />
      </div>
      <div className="title">
        {isMyReservation && <Eclipse style={{ backgroundColor: color }} />}
        <UI.Typography
          type="headline01"
          fontStyle="bold"
          color="var(--alias-color-label-normal)"
          style={{ width: "100%", wordBreak: "break-word" }}
        >
          {reservationInfo?.title ?? ""}
        </UI.Typography>
      </div>
      <div className="content" style={{ overflowY: "auto" }}>
        <ContentContainer>
          <BasicContainer>
            <div className="format">
              <div className="label">
                <UI.Icon name="meetingRoom2" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  회의실
                </UI.Typography>
              </div>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {meetingRoom?.name ?? ""}
              </UI.Typography>
            </div>
            <div className="format">
              <div className="label">
                <UI.Icon name="clock" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  예약 시간
                </UI.Typography>
              </div>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {formatTimeRange(reservationInfo?.startTime ?? "", reservationInfo?.endTime ?? "")}
              </UI.Typography>
            </div>
            <div className="format" style={{ display: reservationInfo?.isRecurring === true ? "flex" : "none" }}>
              <div className="label">
                <UI.Icon name="change" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  반복 조건
                </UI.Typography>
              </div>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {reservationInfo?.rrule ? rruleService.transRRule(reservationInfo?.rrule) : ""}
                {endRrule ? ` | ${getKorDate(endRrule.toString()).replaceAll(".", ". ")} 반복 종료` : " | 무기한 반복"}
              </UI.Typography>
            </div>
          </BasicContainer>
          <div style={{ height: 1, backgroundColor: "var(--alias-color-line-normal)", width: "100%" }} />
          <BasicContainer>
            <div className="people-format">
              <div className="label">
                <UI.Icon name="person" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  예약자
                </UI.Typography>
              </div>
              {reservationInfo?.reservationCreator && (
                <TagContainer primary={reservationInfo?.reservationCreator?.member?.id === memberInfo?.id}>
                  {reservationInfo?.reservationCreator?.member?.image ? (
                    <img src={reservationInfo?.reservationCreator.member?.image} alt="profile" />
                  ) : (
                    <UI.Icon
                      name="person"
                      size={20}
                      color={
                        reservationInfo?.reservationCreator?.member?.id === memberInfo?.id
                          ? "white"
                          : "var(--alias-color-primary-normal)"
                      }
                    />
                  )}
                  <UI.Typography
                    type="label01"
                    fontStyle="medium"
                    color={
                      reservationInfo?.reservationCreator?.member?.id === memberInfo?.id
                        ? "white"
                        : "var(--alias-color-primary-normal)"
                    }
                  >
                    {reservationInfo?.reservationCreator?.member?.nickname ??
                      reservationInfo?.reservationCreator?.visitorEmail}
                  </UI.Typography>
                </TagContainer>
              )}
            </div>
            <div className="people-format">
              <div className="label">
                <UI.Icon name="people" size={16} color="var(--alias-color-label-assistive)" />
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  참석자
                </UI.Typography>
              </div>
              <AttendeeContainer>
                {reservationInfo?.reservationAttendees?.map((attendee, index) => {
                  if (attendee.isVisitor) {
                    return (
                      <UI.Tag
                        type="primary"
                        color="gray"
                        label={attendee.visitorEmail}
                        icon="person"
                        showIcon
                        key={`${attendee.visitorEmail}_${index}`}
                      />
                    );
                  } else {
                    return (
                      <TagContainer primary={attendee.member.id === memberInfo?.id} key={attendee.member.id}>
                        {attendee.member.image ? (
                          <img src={attendee.member.image} alt="profile" />
                        ) : (
                          <UI.Icon
                            name="person"
                            size={20}
                            color={
                              attendee.member.id === memberInfo?.id ? "white" : "var(--alias-color-primary-normal)"
                            }
                          />
                        )}
                        <UI.Typography
                          type="label01"
                          fontStyle="medium"
                          color={attendee.member.id === memberInfo?.id ? "white" : "var(--alias-color-primary-normal)"}
                        >
                          {attendee.member.nickname}
                        </UI.Typography>
                      </TagContainer>
                    );
                  }
                })}
              </AttendeeContainer>
            </div>
          </BasicContainer>
          <div style={{ height: 1, backgroundColor: "var(--alias-color-line-normal)", width: "100%" }} />
        </ContentContainer>
        <NoteContainer>
          <div className="label">
            <UI.Icon name="notes" size={16} color="var(--alias-color-label-assistive)" />
            <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
              예약 메모
            </UI.Typography>
          </div>
          <UI.Typography
            type="body01 normal"
            fontStyle="regular"
            color="var(--alias-color-label-normal)"
            style={{ wordBreak: "break-word" }}
          >
            {removeHtmlTag(reservationInfo?.memo ?? "")}
          </UI.Typography>
        </NoteContainer>
      </div>
      <BtnContainer isShow={canEdit}>
        <UI.Button
          disabled={!isInTimeRange(reservationInfo?.startTime ?? "", reservationInfo?.endTime ?? "")}
          primary={false}
          title="수정하기"
          prefixIcon="modify"
          onClick={() => {
            if (!isInTimeRange(reservationInfo?.startTime ?? "", reservationInfo?.endTime ?? "")) {
              showToast("07:00 - 23:00 이외의 시간대 예약은 수정을 지원하지 않습니다.", "warning");
              return;
            }
            setOpen?.(false);
            onHandleEdit?.(reservationInfo, meetingRoom, isMyReservation);
          }}
          showPrefixIcon
        />
        <UI.Button
          type="outlined"
          primary={false}
          title="삭제하기"
          prefixIcon="trashCan"
          onClick={() => setOpenDeleteModal(true)}
          showPrefixIcon
        />
      </BtnContainer>
      <DeleteWarningModal
        modalIsOpen={reservationInfo?.isRecurring !== true && openDeleteModal}
        closeModal={() => setOpenDeleteModal(false)}
        title={`"${reservationInfo?.title}" 예약을 삭제하시겠습니까?`}
        content={"예약을 삭제하면 복구할 수 없습니다."}
        onChange={onConfirmDelete}
      />
      <Modal.RepeatReservationDeleteModal
        modalIsOpen={reservationInfo?.isRecurring === true && openDeleteModal}
        closeModal={() => setOpenDeleteModal(false)}
        onDelete={(option: RepeatReservationDeleteOption) => {
          onConfirmDelete(option);
        }}
      />
      <LoadingModal modalIsOpen={loading} />
    </Container>
  );
};

export default ReservationInfoModal;

const Container = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  box-sizing: border-box;
  z-index: 10;
  display: flex;
  width: 400px;
  max-height: 600px;
  overflow-y: auto;
  padding: 40px 24px 20px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  border-radius: 16px;
  background: var(--color-background-normal-normal, #fff);
  box-shadow: var(--alias-style-shadow-strong);

  .title {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;

    align-self: stretch;
  }
`;

const Eclipse = styled.div`
  max-width: 12px;
  width: 100%;
  height: 12px;
  border-radius: 50%;
  margin-top: 7px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const BasicContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;

  .format {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
  }

  .label {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .people-format {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
  }
`;

const TagContainer = styled.div<{ primary: boolean }>`
  display: flex;
  padding: 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  background-color: ${(props) => (props.primary ? "var(--alias-color-primary-normal)" : "var(--global-color-blue-95)")};
  border-radius: 40px;

  img {
    width: 20px;
    height: 20px;
    background-color: ${(props) => (props.primary ? "white" : "var(--alias-color-primary-normal)")};
    border-radius: 40px;
    object-fit: cover;
  }
`;

const AttendeeContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const NoteContainer = styled.div`
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  .label {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const BtnContainer = styled.div<{ isShow: boolean }>`
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  display: ${(props) => (props.isShow ? "flex" : "none")};
`;
